<template>
    <article class="article-content">
        <section class="content-section">
            <h2>{{ sections?.intro?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.intro?.content)"
                :key="'email-intro-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section">
            <h2>{{ sections?.two_lanes?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.two_lanes?.content)"
                :key="'email-two-lanes-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure article-figure--narrow">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/disabled_mailing-400w.webp   400w,
                            /images/blog/email-notifications/disabled_mailing-800w.webp   800w,
                            /images/blog/email-notifications/disabled_mailing-1200w.webp 1200w,
                            /images/blog/email-notifications/disabled_mailing-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 70vw, (max-width: 1200px) 56vw, 560px"
                    />
                    <img
                        src="/images/blog/email-notifications/disabled_mailing.png"
                        alt="Login page with password reset disabled when SYSTEM_SMTP_DSN is unset"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.disabled_mailing }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.use_cases?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.use_cases?.content)"
                :key="'email-use-cases-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure article-figure--narrow">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/password_reset-400w.webp   400w,
                            /images/blog/email-notifications/password_reset-800w.webp   800w,
                            /images/blog/email-notifications/password_reset-1200w.webp 1200w,
                            /images/blog/email-notifications/password_reset-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 70vw, (max-width: 1200px) 56vw, 560px"
                    />
                    <img
                        src="/images/blog/email-notifications/password_reset.png"
                        alt="Forgot Password dialog powered by the seeded password_reset system template"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.password_reset_dialog }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.per_item_vs_post_run?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(
                    sections?.per_item_vs_post_run?.content
                )"
                :key="'email-per-item-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/email_transform-400w.webp   400w,
                            /images/blog/email-notifications/email_transform-800w.webp   800w,
                            /images/blog/email-notifications/email_transform-1200w.webp 1200w,
                            /images/blog/email-notifications/email_transform-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/email-notifications/email_transform.png"
                        alt="Workflow Send Email transform configured to fire per item"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.send_email_transform }}</figcaption>
            </figure>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/post_workflow_run-400w.webp   400w,
                            /images/blog/email-notifications/post_workflow_run-800w.webp   800w,
                            /images/blog/email-notifications/post_workflow_run-1200w.webp 1200w,
                            /images/blog/email-notifications/post_workflow_run-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/email-notifications/post_workflow_run.png"
                        alt="Post-Run Send Email action with run context variables and condition selector"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.post_run_email }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.templates?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.templates?.content)"
                :key="'email-templates-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/email_templates_listing-400w.webp   400w,
                            /images/blog/email-notifications/email_templates_listing-800w.webp   800w,
                            /images/blog/email-notifications/email_templates_listing-1200w.webp 1200w,
                            /images/blog/email-notifications/email_templates_listing-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/email-notifications/email_templates_listing.png"
                        alt="Email Templates tab listing system and workflow templates side by side"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.templates_listing }}</figcaption>
            </figure>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/system_settings_email-400w.webp   400w,
                            /images/blog/email-notifications/system_settings_email-800w.webp   800w,
                            /images/blog/email-notifications/system_settings_email-1200w.webp 1200w,
                            /images/blog/email-notifications/system_settings_email-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/email-notifications/system_settings_email.png"
                        alt="Editing the Password Reset system template with auto-detected Handlebars variables"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.template_editor }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.async_delivery_and_logs?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(
                    sections?.async_delivery_and_logs?.content
                )"
                :key="'email-async-logs-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <figure class="article-figure">
                <picture>
                    <source
                        type="image/webp"
                        srcset="
                            /images/blog/email-notifications/system_logs-400w.webp   400w,
                            /images/blog/email-notifications/system_logs-800w.webp   800w,
                            /images/blog/email-notifications/system_logs-1200w.webp 1200w,
                            /images/blog/email-notifications/system_logs-1600w.webp 1600w
                        "
                        sizes="(max-width: 768px) 100vw, (max-width: 1200px) 80vw, 800px"
                    />
                    <img
                        src="/images/blog/email-notifications/system_logs.png"
                        alt="System Logs tab filtered to email_sent entries"
                        loading="lazy"
                    />
                </picture>
                <figcaption>{{ captions?.system_logs }}</figcaption>
            </figure>
        </section>

        <section class="content-section">
            <h2>{{ sections?.setup?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.setup?.content)"
                :key="'email-setup-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
        </section>

        <section class="content-section teaser-section">
            <h2>{{ sections?.whats_next?.title }}</h2>
            <div
                v-for="(paragraph, index) in splitParagraphs(sections?.whats_next?.content)"
                :key="'email-whats-next-' + index"
                class="content-paragraph"
                v-html="formatContent(paragraph)"
            ></div>
            <div class="coming-soon-badge">
                <SmartIcon
                    icon="clock"
                    :size="18"
                />
                {{ t('blog.coming_soon') }}: Webhook Outputs
            </div>
        </section>
    </article>
</template>

<script setup lang="ts">
    import {
        useBlogFormatter,
        type PostSections,
        type PostCaptions,
    } from '@/composables/useBlogFormatter'
    import { useTranslations } from '@/composables/useTranslations'
    import SmartIcon from '@/components/common/SmartIcon.vue'

    defineProps<{
        sections?: PostSections
        captions?: PostCaptions
    }>()

    const { t } = useTranslations()
    const { splitParagraphs, formatContent } = useBlogFormatter()
</script>
